import { diffBlocks, rowId, type Change } from "./autosave.ts";
import type { SnapshotPayload } from "./api";

export const COMPARISON_BLOCKS = ["common", "rewards", "packs", "auctions", "tiles", "spin", "puzzles", "puzzlePrices", "dailyRewards", "socialChannels", "socialEventTypes", "pets", "adoptPools", "petStars", "pettownStars", "pettownSlots", "matchModes", "abilityCards", "battleStages", "talentNodes", "frames", "boardCells", "lampDrops", "rivals", "intimacy", "idolPhotos", "idolVipPhotos", "idolVipVideos", "stages", "stageImages"] as const satisfies readonly (keyof SnapshotPayload["blocks"])[];

const META = new Set(["configVersion", "revision", "status"]);
const BLOCK_NAMES: Record<string, string> = { spin: "spins", packs: "itemPacks", common: "common" };

/** Preserve every returned configuration field. Editor models intentionally omit fields and cannot prove version equality. */
export function comparisonContent(payload: SnapshotPayload): Record<string, unknown> {
  for (const key of COMPARISON_BLOCKS) if (!payload.blocks[key]) throw new Error(`Configuration block unavailable: ${key}`);
  const out: Record<string, unknown> = { levels: payload.levels.levels };
  for (const [key, block] of Object.entries(payload.blocks)) {
    if (!block?.ok) throw new Error(`Configuration block unavailable: ${key}`);
    const value = block.value;
    if (value && !Array.isArray(value) && typeof value === "object" && (("revision" in value && value.revision !== payload.levels.revision) || ("configVersion" in value && value.configVersion !== payload.levels.configVersion))) {
      throw new Error(`Configuration changed while reading: ${key}`);
    }
    if (Array.isArray(value)) { out[BLOCK_NAMES[key] ?? key] = value; continue; }
    if (!value || typeof value !== "object") { out[key] = value; continue; }
    const fields = Object.entries(value).filter(([k]) => !META.has(k));
    if (key === "common") {
      const { common, ...selectors } = Object.fromEntries(fields);
      out.common = common;
      out.selectors = selectors;
    } else if (key === "packs") {
      const packs = (value as { packs: Array<{ kind: string }> }).packs;
      out.itemPacks = packs.filter((p) => p.kind !== "idol");
      out.idolPacks = packs.filter((p) => p.kind === "idol");
      const extra = Object.fromEntries(fields.filter(([k]) => k !== "packs"));
      if (Object.keys(extra).length) out.packSettings = extra;
    } else if (key === "rivals") {
      const data = value as { rivals: unknown[]; cells: Array<Record<string, unknown>>; pettownPrices: Array<Record<string, unknown>> };
      out.rivals = data.rivals;
      out.rivalCells = data.cells.map((r) => ({ ...r, id: `${r.rivalId}|${r.level}|${r.buildingIdx}|${r.starLevel}` }));
      out.pettownRivalPrices = data.pettownPrices.map((r) => ({ ...r, id: `${r.rivalId}|${r.star}` }));
      const extra = Object.fromEntries(fields.filter(([k]) => !["rivals", "cells", "pettownPrices"].includes(k)));
      if (Object.keys(extra).length) out.rivalSettings = extra;
    } else {
      out[BLOCK_NAMES[key] ?? key] = fields.length === 1 ? fields[0][1] : Object.fromEntries(fields);
    }
  }
  return stripIdentity(out) as Record<string, unknown>;
}

/** Allocation IDs are not editable configuration; all business identifiers and content remain. */
function stripIdentity(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(stripIdentity);
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).filter(([k]) => k !== "uid" && k !== "configVersion" && k !== "a0").map(([k, x]) => [k, stripIdentity(x)]));
  return v;
}

export function rawSnapshotChanges(left: SnapshotPayload, right: SnapshotPayload): Change[] {
  const a = comparisonContent(left), b = comparisonContent(right);
  const blocks = [...new Set([...Object.keys(a), ...Object.keys(b)])];
  const changes = diffBlocks(a, b, blocks);
  for (const block of blocks) nullPresence(block, "", a[block], b[block], changes);
  return changes;
}

/** Missing and explicit null can mean different inheritance rules. The editor diff treats them as equivalent. */
function nullPresence(block: string, path: string, a: unknown, b: unknown, out: Change[]): void {
  if (Array.isArray(a) && Array.isArray(b)) {
    const keyed = (xs: unknown[]) => xs.every((x) => rowId(x)) && new Set(xs.map(rowId)).size === xs.length;
    if (keyed(a) && keyed(b)) {
      const old = new Map(a.map((v) => [rowId(v), v]));
      for (const value of b) { const key = rowId(value); if (old.has(key)) nullPresence(block, `${path}[${key}]`, old.get(key), value, out); }
    } else for (let i = 0; i < Math.min(a.length, b.length); i++) nullPresence(block, `${path}[${i}]`, a[i], b[i], out);
  } else if (a && b && typeof a === "object" && typeof b === "object" && !Array.isArray(a) && !Array.isArray(b)) {
    const x = a as Record<string, unknown>, y = b as Record<string, unknown>;
    for (const key of new Set([...Object.keys(x), ...Object.keys(y)])) {
      const at = path ? `${path}.${key}` : key;
      if (!(key in x) && y[key] === null) out.push({ block, path: at, to: null });
      else if (!(key in y) && x[key] === null) out.push({ block, path: at, from: null });
      else nullPresence(block, at, x[key], y[key], out);
    }
  }
}


export function currencyChanges(left: { economyModel?: number; moneyScale?: unknown }, right: { economyModel?: number; moneyScale?: unknown }): Change[] {
  const values = (v: typeof left) => ({ versionSettings: { economyModel: v.economyModel ?? 1, moneyScale: v.moneyScale ?? null } });
  return diffBlocks(values(left), values(right), ["versionSettings"]);
}


export function publishedComparisonProof(left?: { status: string; checksum?: string }, right?: { status: string; checksum?: string }) {
  const available = !!left?.checksum && !!right?.checksum && left.status !== "draft" && right.status !== "draft";
  return { samePublishedContent: available && left.checksum === right.checksum, checksumConflict: available && left.checksum !== right.checksum };
}
